import React, { useState, useRef } from 'react';
import { Plus, Upload, Settings, Keyboard, Disc, Sparkles, X } from 'lucide-react';
import { Wheel, WheelEntry } from '../../types';
import { WheelItem } from './WheelItem';
import { NewWheelModal } from './NewWheelModal';
import { exportWheelToJSON, validateAndParseWheelJSON } from '../../utils/exportImport';

interface WheelSidebarProps {
  wheels: Wheel[];
  activeWheelId: string | null;
  onSelectWheel: (id: string) => void;
  onCreateWheel: (name: string, entries: string[], colorThemeId: string) => void;
  onRenameWheel: (id: string, newName: string) => void;
  onDuplicateWheel: (id: string) => void;
  onDeleteWheel: (id: string) => void;
  onOpenSettings: () => void;
  onOpenShortcuts: () => void;
  currentEntries: WheelEntry[];
  isSpinning?: boolean;
  onClose?: () => void;
}

export const WheelSidebar: React.FC<WheelSidebarProps> = ({
  wheels,
  activeWheelId,
  onSelectWheel,
  onCreateWheel,
  onRenameWheel,
  onDuplicateWheel,
  onDeleteWheel,
  onOpenSettings,
  onOpenShortcuts,
  currentEntries,
  isSpinning = false,
  onClose,
}) => {
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleExportCurrent = (wheel: Wheel) => {
    exportWheelToJSON(wheel, currentEntries);
  };

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      const result = validateAndParseWheelJSON(content);
      if (!result.valid || !result.data) {
        alert(result.error || 'Failed to import wheel JSON.');
        return;
      }

      onCreateWheel(
        result.data.name,
        result.data.entries.map((e) => e.label),
        result.data.colorThemeId || 'vibrant'
      );
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <aside
      className="glass-panel"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: '1.25rem 1rem',
        gap: '1rem',
        width: '100%',
        maxHeight: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Brand Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 0.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              background: 'var(--accent-gradient)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'var(--accent-glow)',
              flexShrink: 0,
            }}
          >
            <Sparkles size={20} color="#ffffff" />
          </div>
          <div>
            <h1
              style={{
                fontSize: '1.25rem',
                fontWeight: 800,
                fontFamily: 'var(--font-display)',
                letterSpacing: '-0.03em',
                background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                lineHeight: 1.1,
              }}
            >
              ChittiWin
            </h1>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600 }}>
              Modern Spin Wheel
            </span>
          </div>
        </div>

        {onClose && (
          <button
            onClick={onClose}
            className="btn-icon mobile-close-btn"
            title="Close Menu"
            aria-label="Close Menu"
            style={{ width: '34px', height: '34px', borderRadius: '8px' }}
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* New Wheel Primary CTA */}
      <button
        onClick={() => setIsNewModalOpen(true)}
        disabled={isSpinning}
        className="btn-primary"
        style={{ width: '100%', padding: '0.65rem 1rem', fontSize: '0.9rem' }}
      >
        <Plus size={18} />
        New Wheel
      </button>

      {/* Saved Wheels List */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div
          style={{
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'var(--text-muted)',
            padding: '0 0.5rem 0.5rem',
          }}
        >
          Saved Wheels ({wheels.length})
        </div>

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            paddingRight: '2px',
          }}
        >
          {wheels.map((w) => (
            <WheelItem
              key={w.id}
              wheel={w}
              isActive={w.id === activeWheelId}
              onSelect={(id) => {
                onSelectWheel(id);
                if (window.innerWidth < 1024 && onClose) {
                  onClose();
                }
              }}
              onRename={onRenameWheel}
              onDuplicate={onDuplicateWheel}
              onExport={handleExportCurrent}
              onDelete={onDeleteWheel}
              isOnlyWheel={wheels.length <= 1}
            />
          ))}
        </div>
      </div>

      {/* Footer Utility Actions */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          paddingTop: '0.75rem',
          borderTop: '1px solid var(--border-subtle)',
        }}
      >
        {/* Hidden File Input for Import */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleImportFile}
          accept=".json"
          style={{ display: 'none' }}
        />

        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isSpinning}
          className="btn-ghost"
          style={{ width: '100%', justifyContent: 'flex-start', fontSize: '0.85rem' }}
        >
          <Upload size={16} />
          Import Wheel
        </button>

        <button
          onClick={onOpenShortcuts}
          className="btn-ghost"
          style={{ width: '100%', justifyContent: 'flex-start', fontSize: '0.85rem' }}
        >
          <Keyboard size={16} />
          Shortcuts
        </button>

        <button
          onClick={onOpenSettings}
          className="btn-ghost"
          style={{ width: '100%', justifyContent: 'flex-start', fontSize: '0.85rem' }}
        >
          <Settings size={16} />
          Settings
        </button>
      </div>

      {/* New Wheel Modal */}
      <NewWheelModal
        isOpen={isNewModalOpen}
        onClose={() => setIsNewModalOpen(false)}
        onCreateWheel={onCreateWheel}
      />
    </aside>
  );
};
